import React from "react";
import { Link } from "react-router-dom";
import "./FeesBenefits.css";

const benefits = [
  {
    number: "01",
    title: "Live Lectures & Structured Learning",
    description:
      "Learn software engineering, full stack development, AI and data science through structured sessions, guided explanations and practical demonstrations.",
  },
  {
    number: "02",
    title: "Assignments, Practice & Independent Research",
    description:
      "Develop problem-solving skills through assignments, coding practice, experimentation and independent research using technical documentation and learning tools.",
  },
  {
    number: "03",
    title: "Real-World Industry Projects",
    description:
      "Design and build complete applications with frontend, backend, APIs, databases and deployment, developing practical skills beyond classroom examples.",
  },
  {
    number: "04",
    title: "GitHub, Resume & Professional Portfolio",
    description:
      "Develop documented GitHub projects, a professional resume, LinkedIn profile and an interview-ready portfolio that demonstrates your abilities.",
  },
  {
    number: "05",
    title: "Interview Preparation & Mock Interviews",
    description:
      "Practice coding assessments, technical questions, project demonstrations, HR discussions and mock interviews with actionable feedback.",
  },
  {
    number: "06",
    title: "Certifications & Professional Documents",
    description:
      "Receive applicable course certification, internship documentation and recommendation letters upon meeting the relevant programme requirements.",
  },
  {
    number: "07",
    title: "Industry Expert Sessions & Mentorship",
    description:
      "Gain industry perspectives through expert guidance, professional discussions, technical mentoring and career development sessions.",
  },
  {
    number: "08",
    title: "Research & Technical Writing Guidance",
    description:
      "Learn research fundamentals, experimentation, result evaluation, technical documentation and research-paper preparation with guidance.",
  },
  {
    number: "09",
    title: "Placement Assistance & Career Growth",
    description:
      "Receive guidance on suitable job opportunities, applications, interview readiness, evaluating offers and planning long-term professional growth.",
  },
];

export default function FeesBenefits() {
  return (
    <main className="fb-page">
      <section className="fb-hero">
        <div className="fb-container">
          <span className="fb-eyebrow">
            FEES &amp; CAREER BLUEPRINT
          </span>

          <h1>
            Your Career. Your Growth.
            <br />
            <span>Your ₹1 Crore Ambition.</span>
          </h1>

          <p className="fb-hero-description">
            A long-term career blueprint designed to help you
            learn technologies, build industry-level products,
            secure suitable job opportunities, develop advanced
            AI expertise and grow into higher-value professional roles.
          </p>

          

          <div className="fb-hero-steps">
            {["LEARN", "BUILD", "GET PLACED", "GROW"].map(
              (step, index) => (
                <div key={step} className="fb-hero-step">
                  <span>0{index + 1}</span>
                  <strong>{step}</strong>
                </div>
              )
            )}
          </div>

          <p className="fb-target-disclaimer">
            ₹1 Crore/year is our ambitious long-term career target. Your success depends on your skills, dedication, performance, opportunities, and individual circumstances.
          </p>
        </div>
      </section>

      <section id="career-plan" className="fb-blueprint">
        <div className="fb-container">

          <div className="fb-section-heading">
            <span className="fb-section-label">
              THE COMPLETE CAREER BLUEPRINT
            </span>
            <h2>Our Plan &amp; Execution Strategy to Reach Your ₹1 Crore/Year Career Goal</h2>

          

            <p>
              A practical, progressive journey focused on learning,
              building, getting job-ready and continuously growing.
              Each stage prepares you for the next.
            </p>
          </div>

<div className="fb-ai-strategy">
            <div className="fb-ai-strategy-label">
              THE THINKING BEHIND OUR LEARNING PATH
            </div>

            <h3>Why Don't We Start Directly With AI?</h3>

            <p className="fb-ai-strategy-lead">
              Because the industry needs more than AI models.
              It needs professionals who can turn AI into
              complete, working products.
            </p>

            <p>
              That's why we first build your foundation in
              frontend development, backend systems, databases,
              APIs, and deployment. These skills help you
              develop end-to-end applications and prepare for
              a wider range of technical interviews and job
              opportunities.
            </p>

            <p>
              Many training programmes attract students by
              promising to teach AI directly. But learning
              AI tools or models alone does not necessarily
              prepare someone for industry roles. Companies
              increasingly value professionals who can design,
              develop, integrate, deploy, and maintain complete
              solutions.
            </p>

            <p>
              That's why our approach focuses on building strong
              engineering foundations before advancing into AI.
              We then introduce advanced AI so you can integrate
              intelligent capabilities into real-world software,
              rather than learning AI in isolation.
            </p>

            <div className="fb-ai-strategy-flow">
              Software Engineering Foundations
              <span>→</span>
              Industry Projects
              <span>→</span>
              Advanced AI Integration
              <span>→</span>
              Career Growth
            </div>
          </div>


          <div className="fb-blueprint-timeline">

            <article className="fb-blueprint-item">
              <div className="fb-blueprint-marker">01</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">FOUNDATION</span>
                <h3>Learn the Technologies</h3>
                <p>
                  Begin with the fundamentals of software development.
                  Understand how applications are designed, developed,
                  connected and maintained.
                </p>
                <div className="fb-tech-list">
                  {[
                    "Linux", "HTML", "CSS", "Python", "FastAPI",
                    "JavaScript", "React.js", "MongoDB", "GitHub"
                  ].map((tech) => (
                    <span key={tech}>{tech}</span>
                  ))}
                </div>

                <div className="fb-tech-purpose">
                  <h4>How These Technologies Work Together</h4>
                  <p>
                    Linux provides the development environment.
                    HTML, CSS, JavaScript, and React.js create
                    the user experience. Python and FastAPI
                    power the backend and APIs. MongoDB manages
                    application data, while GitHub supports
                    version control and collaboration.
                  </p>
                  <p>
                    Together, these skills prepare you to build
                    complete software products and later
                    integrate advanced AI capabilities.
                  </p>
                </div>

              </div>
            </article>

            <article className="fb-blueprint-item">
              <div className="fb-blueprint-marker">02</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">PRACTICAL DEVELOPMENT</span>
                <h3>Build Real-World, Industry-Level Projects</h3>

                <div className="fb-project-detail">
                  <p>
                    Move beyond tutorials and basic assignments.
                    Design, develop, test, and deploy complete
                    applications that solve real-world problems
                    and demonstrate your technical abilities.
                  </p>

                  <ul>
                    <li>Frontend, backend, API and database integration</li>
                    <li>Authentication, dashboards and real workflows</li>
                    <li>AI-powered features as learning progresses</li>
                    <li>GitHub repositories, documentation and deployment</li>
                    <li>Portfolio-ready projects for technical interviews</li>
                  </ul>

                  <strong>
                    Don't just tell companies what you know.
                    Show them what you can build.
                  </strong>
                </div>
                
                
              </div>
            </article>

            <article className="fb-blueprint-item">
              <div className="fb-blueprint-marker">03</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">JOB READINESS</span>
                <h3>Prepare for Professional Interviews &amp; Become Job-Ready</h3>
                <p>
                  Learn to demonstrate your skills, explain projects
                  confidently, and compete for professional opportunities.
                </p>

                <div className="fb-interview-details">
                  <ul>
                    <li>Technical interviews and coding assessments</li>
                    <li>Mock interviews with personalized feedback</li>
                    <li>Professional resume, LinkedIn and GitHub portfolio</li>
                    <li>Industry-project demonstrations and discussions</li>
                    <li>HR interviews, communication and salary discussions</li>
                    <li>Mentor-guided preparation for relevant job openings</li>
                  </ul>
                </div>
              </div>
            </article>

            <article className="fb-blueprint-item">
              <div className="fb-blueprint-marker">04</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">OPPORTUNITY SEARCH</span>
                <h3>Apply for Jobs With Mentor Guidance</h3>

                <p>
                  Start pursuing suitable software engineering opportunities
                  while continuing your advanced AI learning in parallel.
                  Apply strategically with support from your mentors.
                </p>

                <div className="fb-application-details">
                  <ul>
                    <li>Identify relevant job openings and suitable companies</li>
                    <li>Tailor your resume and portfolio to each opportunity</li>
                    <li>Apply through company websites and professional platforms</li>
                    <li>Track applications, assessments and interview progress</li>
                    <li>Prepare for interviews with mentor guidance</li>
                    <li>Continue building advanced AI skills alongside your job search</li>
                  </ul>
                </div></div>
            </article>

            <article className="fb-blueprint-item fb-blueprint-ai">
              <div className="fb-blueprint-marker">05</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">PARALLEL ADVANCEMENT</span>
                <h3>Advance Your Skills in Artificial Intelligence</h3>
                <p>
                  Continue studying AI while pursuing software engineering
                  opportunities. Build on your full-stack foundation to
                  develop intelligent, production-oriented applications.
                </p>

                <div className="fb-ai-learning-details">
                  <ul>
                    <li>Machine Learning and Deep Learning</li>
                    <li>Natural Language Processing and Computer Vision</li>
                    <li>Generative AI and Large Language Models</li>
                    <li>Retrieval-Augmented Generation (RAG) and AI Agents</li>
                    <li>Model integration, evaluation and MLOps</li>
                    <li>Practical AI-powered applications and projects</li>
                  </ul>
                </div>
                <div className="fb-tech-list">
                  {[
                    "Machine Learning",
                    "Deep Learning",
                    "Computer Vision",
                    "NLP",
                    "Generative AI",
                    "LLMs",
                    "AI Agents",
                    "MLOps"
                  ].map((tech) => (
                    <span key={tech}>{tech}</span>
                  ))}
                </div>
                <p>
                  As your AI skills improve, explore suitable
                  AI-related opportunities alongside software roles.
                </p>
              </div>
            </article>

            <article className="fb-blueprint-item">
              <div className="fb-blueprint-marker">06</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">FIRST CAREER MILESTONE</span>
                <h3>Evaluate and Join the Right Company</h3>
                <p>
                  Your first professional opportunity should help you
                  build experience, develop valuable skills, and move
                  toward your long-term career goals.
                </p>

                <div className="fb-company-evaluation">
                  <ul>
                    <li>Job role, responsibilities and career direction</li>
                    <li>Salary package, benefits and compensation structure</li>
                    <li>Technologies, projects and practical experience</li>
                    <li>Company stability, culture and work environment</li>
                    <li>Mentorship, learning and promotion opportunities</li>
                    <li>Potential for future AI and engineering career growth</li>
                  </ul>

                  <strong>
                    Choose a role that helps you grow professionally,
                    not only one that offers an immediate salary.
                  </strong>
                </div>
              </div>
            </article>

            <article className="fb-blueprint-item fb-blueprint-last">
              <div className="fb-blueprint-marker">07</div>
              <div className="fb-blueprint-content">
                <span className="fb-blueprint-tag">LONG-TERM CAREER GROWTH</span>
                <h3>Grow Toward High-Value Roles &amp; Your ₹1 Crore/Year Career Goal</h3>
                <p>
                  Your first job is the beginning of your career.
                  Continue developing valuable expertise, taking
                  greater responsibilities, and pursuing opportunities
                  that support long-term professional growth.
                </p>

                <div className="fb-career-growth-details">
                  <ul>
                    <li>Gain professional experience on real industry projects</li>
                    <li>Develop advanced AI and software architecture expertise</li>
                    <li>Progress toward senior, specialist and leadership roles</li>
                    <li>Solve complex, high-impact business problems</li>
                    <li>Evaluate career opportunities and compensation strategically</li>
                    <li>Continue learning and pursuing higher-value opportunities</li>
                  </ul>

                  <strong>
                    Your first job starts your career. Continuous learning,
                    valuable expertise, and strategic decisions shape
                    how far you can grow.
                  </strong>
                </div>
                <div className="fb-blueprint-goal">
                  <strong>THE LONG-TERM AMBITION</strong>
                  <span>₹1 Crore / Year</span>
                  <small>
                    ₹1 Crore/year is our ambitious long-term career target. Your success depends on your skills, dedication, performance, opportunities, and individual circumstances.
                  </small>
                </div>
              </div>
            </article>

          </div>
        </div>
      </section>

      <section id="certifications" className="fb-cert-section">
        <div className="fb-container">

          <div className="fb-section-heading">
            <span className="fb-section-label">
              PROFESSIONAL RECOGNITION
            </span>

            <h2>Certifications, Credentials &amp; Career Assets</h2>

            <p>
              Your learning journey should lead to more than
              knowledge. Build credible evidence of your skills,
              project experience and professional development.
            </p>
          </div>

          <div className="fb-cert-grid">

            <article className="fb-cert-card">
              <span className="fb-cert-icon">01</span>
              <h3>Course Completion Certificate</h3>
              <div className="fb-credential-preview">
                <a
                  href="/certificates/course-completion.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View full sample of Course Completion Certificate"
                >
                  <img
                    src="/certificates/course-completion.png"
                    alt="Illustrative sample: Course Completion Certificate"
                    loading="lazy"
                  />
                  <span>View Full Sample ↗</span>
                </a>
              </div>

              <div className="fb-credential-value">
                <div>
                  <h4>What You'll Receive</h4>
                  <p>A professional certificate documenting successfully completed course training, practical assignments and required assessments.</p>
                </div>
                <div>
                  <h4>Why It Matters</h4>
                  <p>Demonstrates your technical training, strengthens your resume and LinkedIn profile, and supports applications for jobs and internships.</p>
                </div>
              </div>

              
            </article>

            <article className="fb-cert-card">
              <span className="fb-cert-icon">02</span>
              <h3>Internship Certificate</h3>
              <div className="fb-credential-preview">
                <a
                  href="/certificates/internship.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View full sample of Internship Certificate"
                >
                  <img
                    src="/certificates/internship.png"
                    alt="Illustrative sample: Internship Certificate"
                    loading="lazy"
                  />
                  <span>View Full Sample ↗</span>
                </a>
              </div>

              <div className="fb-credential-value">
                <div>
                  <h4>What You'll Receive</h4>
                  <p>For eligible students completing a genuine internship, a document describing the internship period, project contributions and responsibilities.</p>
                </div>
                <div>
                  <h4>Why It Matters</h4>
                  <p>Helps recruiters understand your practical exposure, professional responsibilities and experience working on real projects.</p>
                </div>
              </div>

              
            </article>

            <article className="fb-cert-card">
              <span className="fb-cert-icon">03</span>
              <h3>Professional Recommendation Letter</h3>
              <div className="fb-credential-preview">
                <a
                  href="/certificates/recommendation-letter.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View full sample of Professional Recommendation Letter"
                >
                  <img
                    src="/certificates/recommendation-letter.png"
                    alt="Illustrative sample: Professional Recommendation Letter"
                    loading="lazy"
                  />
                  <span>View Full Sample ↗</span>
                </a>
              </div>

              <div className="fb-credential-value">
                <div>
                  <h4>What You'll Receive</h4>
                  <p>For qualifying students, a personalized letter based on observed technical abilities, project performance and professional conduct.</p>
                </div>
                <div>
                  <h4>Why It Matters</h4>
                  <p>Highlights demonstrated strengths and provides useful professional context for job applications and further studies.</p>
                </div>
              </div>

              
            </article>

            <article className="fb-cert-card">
              <span className="fb-cert-icon">04</span>
              <h3>Research Paper Guidance</h3>
              <p>
                Guidance on research methodology, literature
                review, experimentation, technical writing
                and preparation of research manuscripts.
              </p>
            </article>

            <article className="fb-cert-card">
              <span className="fb-cert-icon">05</span>
              <h3>Master Project Portfolio</h3>
              <p>
                A documented industry-level project supported
                by GitHub repositories, technical documentation,
                practical demonstrations and project explanations.
              </p>
            </article>

            <article className="fb-cert-card">
              <span className="fb-cert-icon">06</span>
              <h3>Professional Skill Development</h3>
              <p>
                Build demonstrable technical, problem-solving,
                communication and interview skills that can
                support future employment opportunities.
              </p>
            </article>

          </div>

          <div className="fb-cert-note">
            <strong>Important:</strong> Certificates and recommendation
            letters depend on applicable eligibility and completion
            requirements. Research publication, internship opportunities
            and employment outcomes are not automatically guaranteed.
          </div>

        </div>
      </section>

      <section id="included-benefits" className="fb-section fb-benefits-section">
        <div className="fb-container">
          <div className="fb-section-heading">
            <span className="fb-section-label">MORE THAN LECTURES</span>
            <h2>Everything Included in Your Programme</h2>
            <p>
              A structured approach combining technical skills,
              independent problem-solving, projects and preparation
              for real professional opportunities.
            </p>
          </div>

          <div className="fb-benefits-grid">
            {benefits.map((benefit) => (
              <article key={benefit.number} className="fb-benefit-card">
                <span className="fb-benefit-number">
                  {benefit.number}
                </span>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="fee-structure"
        className="fb-section fb-two-stage-fees"
      >
        <div className="fb-container">

          <div className="fb-section-heading">
            <span className="fb-section-label">
              YOUR INVESTMENT
            </span>

            <h2>Two Payment Stages. One Career Blueprint.</h2>

            <p>
              Begin with structured learning, industry projects,
              and career preparation. The separate placement fee
              applies after joining your first qualifying job,
              according to the agreed terms.
            </p>
          </div>

          <div className="fb-fees-grid fb-two-fee-grid">

            <article className="fb-fee-card fb-featured">
              <div className="fb-fee-topline">
                STAGE 01 · BEFORE STARTING
              </div>

              <h3>Education &amp; Career Preparation</h3>

              <div className="fb-price">
                ₹32,500
              </div>

              <div className="fb-tax-included">
                GST INCLUDED
              </div>

              <p>
                Includes technical lectures, assignments,
                practical exercises, industry-level project
                guidance, GitHub portfolio development, interview
                and mock interview preparation, certifications
                where eligible, and career mentoring.
              </p>

              <div className="fb-fee-bottom">
                Payable before starting the programme.
              </div>
            </article>

            <article className="fb-fee-card">
              <div className="fb-fee-topline">
                STAGE 02 · AFTER FIRST QUALIFYING JOB
              </div>

              <h3>Placement Assistance Fee</h3>

              <div className="fb-price fb-salary-price">
                First Month's
                <br />
                <span>Salary</span>
              </div>

              <p>
                After joining your first qualifying job,
                the agreed placement-assistance fee is
                equivalent to one month's salary,
                calculated according to the official
                placement agreement.
              </p>

              <div className="fb-fee-bottom">
                Payable after qualifying job joining,
                subject to the agreed terms.
              </div>
            </article>

          </div>

          <div className="fb-investment-note">
            <strong>Clear &amp; Transparent Fees:</strong>
            ₹32,500 includes GST. The placement assistance fee
            is separate and applies after qualifying job joining.
            Its calculation, GST treatment, eligibility and payment
            conditions are defined in the written agreement
            provided before enrollment.
          </div>

        </div>
      </section>

      <section className="fb-final-cta">
        <div className="fb-container fb-final-cta-inner">

          <span className="fb-final-eyebrow">
            YOUR NEXT STEP
          </span>

          <h2>
            Your Career Ambition Deserves
            <span> a Clear Plan.</span>
          </h2>

          <p>
            You've explored the technologies, industry-level
            projects, career preparation, certifications and
            programme investment.
          </p>

          <p>
            Now begin by understanding your current skills,
            strengths, career goals and the next steps
            suitable for your background.
          </p>

          <div className="fb-final-actions">
            <Link
              to="/counselling"
              className="fb-primary-btn"
            >
              Explore Career Counselling
              <span>↗</span>
            </Link>

            <Link
              to="/study-roadmap"
              className="fb-outline-btn"
            >
              Review Study Roadmap
            </Link>
          </div>

          <div className="fb-final-footnote">
            Career outcomes depend on individual performance,
            practical skills, employer requirements and
            available opportunities.
          </div>

        </div>
      </section>
    </main>
  );
}
